import type React from "react";
import { GraphemeSvgGlyphFontFallback } from "#grapheme-svg-glyph-font-fallback";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, Text, View } from "@-/com.npmjs.react-native";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import { useVisualActivity } from "@-/dev.improve.haniverse.lib.visual-activity";
import {
  cancelAnimation,
  runOnJS,
  runOnUI,
  useAnimatedReaction,
  useDerivedValue,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
  type SharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { GraphemeGlyphCanvas } from "#grapheme-glyph-canvas";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { SVGPathData } from "@-/com.npmjs.svg-pathdata";
import type {
  GraphemeGlyphRenderStroke,
  GraphemeGlyphCanvasProps,
  GraphemeSvgGlyphStroke,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg.entities";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";
import {
  buildGraphemeSvgGlyphMedianPathData,
  computeGraphemeSvgGlyphMedianLength,
  getGraphemeSvgGlyphStrokeAnimationDurationMs,
  getGraphemeSvgGlyphStepProgress,
  getGraphemeSvgGlyphStrokeBoundaryProgress,
  GRAPHEME_SVG_GLYPH_CELL_UNITS,
  graphemeSvgGlyphHanziWriterEasing,
  GRAPHEME_SVG_GLYPH_PAUSE_BETWEEN_STROKES_MS,
  GRAPHEME_SVG_GLYPH_STROKE_FADE_DURATION_MS,
} from "#grapheme-svg-glyph-animation";

export interface GraphemeSvgGlyphPlayerProps {
  /** Start drawing when visible; inactive prepared card surfaces can defer autoplay. */
  autoPlay?: boolean;
  /** When true, the mini player is visible on first render; tapping the glyph still toggles it. */
  defaultShowPlayer?: boolean;
  fallback?: React.ReactNode;
  glyph: GraphemeGlyph;
  /** Keep the completed glyph visible when false. */
  loop?: boolean;
  /**
   * A UI-thread "this glyph just became visible" flag. When it turns true the player starts
   * drawing from a worklet, without waiting for React to deliver `autoPlay`. The Learn deck
   * uncovers its next card from the UI thread the moment a rating's exit begins, while the JS
   * thread is at its busiest (saving the review, refetching, promoting, mounting the following
   * card) — an `autoPlay` prop alone reaches the player only after all of that, which left the
   * uncovered card sitting on its grey ghost strokes. `autoPlay` still governs stopping, replays
   * and every caller that has no such flag.
   */
  playSignal?: SharedValue<boolean>;
  /** Changing this value immediately restarts the autoplay cycle. */
  replayKey?: number;
  showBackground?: boolean;
  showStrokeControls?: boolean;
  /** Show one cumulative preview per stroke, all driven by the same player timeline. */
  showStrokeSteps?: boolean;
  size?: number;
  /**
   * Hanzi Writer outlines + medians in stroke order. `null` = the grapheme has NO stroke data: the
   * font glyph renders in the strokes' footprint (the deliberate no-strokes state).
   */
  strokeList: readonly GraphemeSvgGlyphStroke[] | null;
}

/** Reanimated `useAnimatedProps` does not drive react-native-svg on web (no setNativeProps). */
const IS_WEB = Platform.OS === "web";

function getCodePoint(grapheme: string): number | null {
  return grapheme.codePointAt(0) ?? null;
}

interface GraphemeSvgGlyphAnimationPlan {
  /** Pause on the finished glyph before a looping cycle fades out. */
  holdBeforeFadeMs: number;
  loop: boolean;
  /** One entry per stroke: wait `delayMs`, then draw to `toValue` over `durationMs`. */
  steps: readonly { delayMs: number; durationMs: number; toValue: number }[];
}

/**
 * Restart the autoplay timeline from an empty glyph. A worklet over plain data so the UI thread
 * can start a glyph by itself; the JS thread reaches it through `runOnUI`.
 */
function startGraphemeSvgGlyphAnimationPlan(
  progress: SharedValue<number>,
  plan: GraphemeSvgGlyphAnimationPlan,
): void {
  "worklet";
  cancelAnimation(progress);
  progress.set(0);
  if (plan.steps.length === 0) {
    return;
  }
  const strokes = plan.steps.map((step) => {
    const draw = withTiming(step.toValue, {
      duration: step.durationMs,
      easing: graphemeSvgGlyphHanziWriterEasing,
    });
    return step.delayMs > 0 ? withDelay(step.delayMs, draw) : draw;
  });
  if (!plan.loop) {
    progress.set(withSequence(...strokes));
    return;
  }
  progress.set(
    withRepeat(
      withSequence(
        ...strokes,
        withDelay(
          plan.holdBeforeFadeMs,
          withTiming(2, {
            duration: GRAPHEME_SVG_GLYPH_STROKE_FADE_DURATION_MS,
            easing: graphemeSvgGlyphHanziWriterEasing,
          }),
        ),
        withTiming(0, { duration: 0 }),
      ),
      -1,
      false,
    ),
  );
}

/**
 * Plays a grapheme's strokes in order (Hanzi Writer style). A grapheme with no stroke data
 * (`strokeList === null`) renders the font glyph instead — there is nothing to animate.
 */
export function GraphemeSvgGlyphPlayer({
  fallback,
  glyph,
  strokeList,
  ...props
}: GraphemeSvgGlyphPlayerProps) {
  const { showBackground = true, size = 180 } = props;
  if (strokeList === null || !getCodePoint(glyph.original) || hasLength(strokeList, 0)) {
    return (
      <GraphemeSvgGlyphFontFallback
        fallback={fallback}
        glyph={glyph}
        showBackground={showBackground}
        size={size}
      />
    );
  }
  return (
    <GraphemeSvgGlyphStrokePlayer
      {...props}
      columnCount={1}
      glyphKey={glyph.original}
      strokeList={strokeList}
    />
  );
}

export interface GraphemeSvgGlyphStripCell {
  glyph: GraphemeGlyph;
  /** `null` = no stroke data: the cell shows the font glyph on the paper instead. */
  strokeList: readonly GraphemeSvgGlyphStroke[] | null;
}

export interface GraphemeSvgGlyphStripPlayerProps extends Pick<
  GraphemeSvgGlyphPlayerProps,
  "autoPlay" | "loop" | "playSignal" | "replayKey" | "showBackground"
> {
  /** One cell per grapheme, in writing order. */
  cellList: readonly GraphemeSvgGlyphStripCell[];
  /** Upper bound for each cell; cells shrink so the whole strip fits its container's width. */
  maxCellSize?: number;
}

/**
 * Writes a whole word on one strip of paper: every grapheme's strokes play in a single timeline on
 * a single canvas, the pen moving left to right from cell to cell.
 */
export function GraphemeSvgGlyphStripPlayer({
  cellList,
  maxCellSize = 140,
  ...props
}: GraphemeSvgGlyphStripPlayerProps) {
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const columnCount = Math.max(1, arrayLength(cellList));
  const cellSize =
    availableWidth === null
      ? null
      : Math.max(1, Math.floor(Math.min(maxCellSize, availableWidth / columnCount)));
  const glyphKey = useMemo(() => cellList.map(({ glyph }) => glyph.original).join(""), [cellList]);
  const strokeList = useMemo(
    () =>
      cellList.flatMap(({ strokeList: cellStrokeList }, index) =>
        (cellStrokeList ?? []).map((stroke) =>
          translateGraphemeSvgGlyphStroke(stroke, index * GRAPHEME_SVG_GLYPH_CELL_UNITS),
        ),
      ),
    [cellList],
  );
  return (
    <View
      className="w-full items-center"
      onLayout={(event) => setAvailableWidth(event.nativeEvent.layout.width)}
    >
      {cellSize !== null && (
        <GraphemeSvgGlyphStrokePlayer
          {...props}
          columnCount={columnCount}
          glyphKey={glyphKey}
          showStrokeControls={false}
          size={cellSize}
          strokeList={strokeList}
        >
          {cellList.map(({ glyph, strokeList: cellStrokeList }, index) =>
            cellStrokeList === null || hasLength(cellStrokeList, 0) ? (
              <View
                className="absolute top-0 items-center justify-center"
                key={index}
                pointerEvents="none"
                style={{ height: cellSize, left: index * cellSize, width: cellSize }}
              >
                <GraphemeFontGlyph
                  className="text-foreground"
                  glyph={{ ...glyph, color: null }}
                  style={{
                    fontSize: Math.round(cellSize * 0.62),
                    lineHeight: Math.round(cellSize * 0.62 * 1.2),
                  }}
                />
              </View>
            ) : null,
          )}
        </GraphemeSvgGlyphStrokePlayer>
      )}
    </View>
  );
}

/** Shift a glyph-space stroke into its strip cell; y is unchanged since every cell shares a row. */
function translateGraphemeSvgGlyphStroke(
  stroke: GraphemeSvgGlyphStroke,
  offsetX: number,
): GraphemeSvgGlyphStroke {
  if (offsetX === 0) {
    return stroke;
  }
  return {
    median: stroke.median.map(([x, y]) => [x + offsetX, y]),
    path: new SVGPathData(stroke.path).translate(offsetX, 0).encode(),
  };
}

type GraphemeSvgGlyphStrokePlayerProps = Omit<
  GraphemeSvgGlyphPlayerProps,
  "fallback" | "glyph" | "strokeList"
> & {
  /** Absolutely positioned content drawn over the strip (e.g. font glyphs for stroke-less cells). */
  children?: React.ReactNode;
  columnCount: number;
  glyphKey: string;
  /** Strokes in strip units, in writing order across every cell. */
  strokeList: readonly GraphemeSvgGlyphStroke[];
};

function GraphemeSvgGlyphStrokePlayer({
  autoPlay = true,
  children,
  columnCount,
  defaultShowPlayer = false,
  glyphKey,
  loop = true,
  playSignal,
  replayKey = 0,
  showBackground = true,
  showStrokeControls = true,
  showStrokeSteps = false,
  size = 180,
  strokeList,
}: GraphemeSvgGlyphStrokePlayerProps) {
  const isVisuallyActive = useVisualActivity();
  const colorScheme = useTheme();
  const isDarkMode = colorScheme === "dark";
  const strokeRenderList = useMemo<GraphemeGlyphRenderStroke[]>(
    () =>
      strokeList.map((stroke) => ({
        length: Math.max(1, computeGraphemeSvgGlyphMedianLength(stroke.median)),
        median: stroke.median,
        medianPathData: buildGraphemeSvgGlyphMedianPathData(stroke.median),
        outlinePath: stroke.path,
      })),
    [strokeList],
  );

  const totalStrokes = arrayLength(strokeRenderList);

  // Overall progress: 0..1 across all strokes (each stroke gets an equal share of the timeline).
  const progress = useSharedValue(0);

  const [showPlayer, setShowPlayer] = useState(defaultShowPlayer);
  const [webProgress, setWebProgress] = useState(0);
  // Which numbered chip is highlighted: the stroke currently being drawn, or the last completed one
  // when the timeline is parked at a segment boundary.
  const [activeStrokeIndex, setActiveStrokeIndex] = useState(0);
  const [availableWidth, setAvailableWidth] = useState(size * 3);

  // The reaction's prepared value is the DERIVED stroke index (plus the raw progress on web, whose
  // geometry is React-driven), so native sends a JS notification only at stroke boundaries,
  // and only while the numbered controls are visible.
  // A per-frame `runOnJS` from every playing glyph queues 60 calls a second onto the JS thread,
  // which is exactly the thread a card promotion needs free.
  useAnimatedReaction(
    () => {
      const value = progress.get();
      const scaled = value * totalStrokes;
      // At an exact boundary (e.g. progress 3/4 with 4 strokes) `floor` would point at the stroke
      // that hasn't started yet, so bias back one to keep the just-finished stroke lit.
      const index = Math.max(0, Math.min(totalStrokes - 1, Math.ceil(scaled) - 1));
      return {
        controlsVisible: showStrokeSteps || (showStrokeControls && showPlayer),
        index,
        value: IS_WEB ? value : 0,
      };
    },
    (current, previous) => {
      if (IS_WEB) {
        runOnJS(setWebProgress)(current.value);
      }
      if (
        current.controlsVisible &&
        (previous?.index !== current.index || !previous?.controlsVisible)
      ) {
        runOnJS(setActiveStrokeIndex)(current.index);
      }
    },
  );
  // The whole autoplay timeline as plain data, so the SAME worklet can start it from either thread.
  // A strip is one continuous timeline: the pen moves on to the next cell after the same pause
  // it takes between two strokes of one character.
  const animationPlan = useMemo<GraphemeSvgGlyphAnimationPlan>(
    () => ({
      holdBeforeFadeMs: GRAPHEME_SVG_GLYPH_PAUSE_BETWEEN_STROKES_MS,
      loop,
      steps: strokeRenderList.map((stroke, index) => ({
        delayMs: index === 0 ? 0 : GRAPHEME_SVG_GLYPH_PAUSE_BETWEEN_STROKES_MS,
        durationMs: Math.max(1, getGraphemeSvgGlyphStrokeAnimationDurationMs(stroke.length)),
        toValue: getGraphemeSvgGlyphStrokeBoundaryProgress(index + 1, totalStrokes),
      })),
    }),
    [loop, strokeRenderList, totalStrokes],
  );

  // Set while the running timeline was started by `playSignal`, so `autoPlay` arriving later (it
  // always does: React learns about a promotion after the UI thread) does not restart a drawing
  // that is already under way.
  const isStartedBySignal = useSharedValue(false);

  const stopAnimation = useCallback(() => {
    cancelAnimation(progress);
  }, [progress]);

  // Let the Worklets plugin capture dependencies. Reanimated appends hashes to an explicit
  // dependency array; React Compiler can reuse that array, growing the saved effect deps on
  // every render until a visibility change replaces it with a shorter array.
  useAnimatedReaction(
    () => isVisuallyActive && (playSignal?.get() ?? false),
    (isVisible, wasVisible) => {
      if (isVisible === wasVisible) {
        return;
      }
      if (isVisible) {
        isStartedBySignal.set(true);
        startGraphemeSvgGlyphAnimationPlan(progress, animationPlan);
        return;
      }
      if (wasVisible === true) {
        // Covered again (an Undo handed the stage back): rewind so the next uncovering replays.
        isStartedBySignal.set(false);
        cancelAnimation(progress);
        progress.set(0);
      }
    },
  );

  /**
   * Jump the timeline so every stroke before `index` is already drawn, then animate just that one
   * stroke in. This is what a numbered chip does: instant context, animated focus.
   */
  const playSingleStroke = useCallback(
    (index: number) => {
      if (!isVisuallyActive) {
        return;
      }
      stopAnimation();
      if (totalStrokes === 0) {
        return;
      }
      const startValue = getGraphemeSvgGlyphStrokeBoundaryProgress(index, totalStrokes);
      progress.set(startValue);
      if (IS_WEB) {
        setWebProgress(startValue);
      }
      setActiveStrokeIndex(index);
      const stroke = strokeRenderList[index];
      if (stroke === undefined) {
        return;
      }
      progress.set(
        withTiming(getGraphemeSvgGlyphStrokeBoundaryProgress(index + 1, totalStrokes), {
          duration: getGraphemeSvgGlyphStrokeAnimationDurationMs(stroke.length),
          easing: graphemeSvgGlyphHanziWriterEasing,
        }),
      );
    },
    [isVisuallyActive, progress, stopAnimation, strokeRenderList, totalStrokes],
  );

  // `autoPlay` starts a glyph that no signal has started, restarts on a replay or a new glyph, and
  // stops a glyph the learner has left. It never stops a glyph that has not been active yet: a
  // deck successor is uncovered (and signalled) while React still calls it inactive.
  const hasBeenActiveRef = useRef(false);
  const startedPlanRef = useRef<{ plan: GraphemeSvgGlyphAnimationPlan; replayKey: number } | null>(
    null,
  );
  useEffect(() => {
    if (!autoPlay || !isVisuallyActive) {
      // A successor may have started from the UI signal before React ever marked it active.
      // Blur/background must cancel that timeline too, including an infinite repeat.
      if (!isVisuallyActive || hasBeenActiveRef.current) {
        hasBeenActiveRef.current = false;
        startedPlanRef.current = null;
        runOnUI(() => {
          isStartedBySignal.set(false);
          cancelAnimation(progress);
          progress.set(0);
        })();
        if (IS_WEB) {
          setWebProgress(0);
        }
      }
      return;
    }
    hasBeenActiveRef.current = true;
    const previous = startedPlanRef.current;
    const isRestart =
      previous !== null && (previous.plan !== animationPlan || previous.replayKey !== replayKey);
    startedPlanRef.current = { plan: animationPlan, replayKey };
    if (IS_WEB) {
      setWebProgress(0);
    }
    runOnUI(() => {
      if (!isRestart && isStartedBySignal.get()) {
        return;
      }
      isStartedBySignal.set(false);
      startGraphemeSvgGlyphAnimationPlan(progress, animationPlan);
    })();
  }, [animationPlan, autoPlay, isStartedBySignal, isVisuallyActive, progress, replayKey]);
  useEffect(
    () => () => {
      cancelAnimation(progress);
    },
    [progress],
  );

  const handleGlyphPress = useCallback(() => {
    setShowPlayer((prev) => !prev);
  }, []);

  const strokeNumberList = useMemo(
    () => Array.from({ length: totalStrokes }, (_, index) => index),
    [totalStrokes],
  );

  const strokeFill = isDarkMode ? "#FFFFFF" : "#000000";
  const upcomingFill = isDarkMode ? "#344155" : "#EEEEEE";

  const guidePalette = useMemo(
    () =>
      isDarkMode
        ? {
            background: "#0B1325",
            frame: "#29374D",
            grid: "#475569",
            gridOpacity: 0.35,
          }
        : {
            background: "#FFFFFF",
            frame: "#CCE4EF",
            grid: "#C4E4F2",
            gridOpacity: 0.5,
          },
    [isDarkMode],
  );

  if (showStrokeSteps) {
    const stepColumns = availableWidth >= 600 ? 5 : availableWidth >= 420 ? 4 : 3;
    const stepSize = Math.max(
      1,
      Math.floor((availableWidth - 12 * (stepColumns - 1)) / stepColumns),
    );
    const stepRows = Array.from({ length: Math.ceil(totalStrokes / stepColumns) }, (_, row) =>
      Array.from({ length: stepColumns }, (_, column) => row * stepColumns + column),
    );
    return (
      <View
        className="w-full items-center gap-3"
        onLayout={(event) => setAvailableWidth(event.nativeEvent.layout.width)}
      >
        {stepRows.map((row, rowIndex) => (
          <View className="w-full flex-row gap-3" key={rowIndex}>
            {row.map((index) =>
              index < totalStrokes ? (
                <GraphemeSvgGlyphStrokeStep
                  canvasProps={{
                    columnCount: 1,
                    glyphKey,
                    guidePalette,
                    highlightedStrokeFill: isDarkMode ? "#FF7774" : "#F34E52",
                    highlightedStrokeIndex: index,
                    isVisuallyActive,
                    progress,
                    progressValue: IS_WEB ? webProgress : undefined,
                    showBackground,
                    size: stepSize,
                    strokeFill,
                    strokeRenderList,
                    upcomingFill,
                  }}
                  index={index}
                  isActive={index === activeStrokeIndex}
                  key={index}
                  onPress={playSingleStroke}
                />
              ) : (
                <View className="flex-1" key={index} />
              ),
            )}
          </View>
        ))}
        <Pressable
          accessibilityLabel="Replay all strokes"
          accessibilityRole="button"
          className="mt-1 rounded-xs border border-border px-4 py-2 active:bg-accent"
          onPress={() => runOnUI(startGraphemeSvgGlyphAnimationPlan)(progress, animationPlan)}
        >
          <Text className="text-sm text-foreground">Replay</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View className="items-center">
      <Pressable disabled={!showStrokeControls} onPress={handleGlyphPress}>
        <View
          className={
            showBackground
              ? "overflow-hidden rounded-xs border border-border bg-surface"
              : undefined
          }
        >
          <View
            className="items-center justify-center"
            style={{ height: size, width: size * columnCount }}
          >
            <GraphemeGlyphCanvas
              columnCount={columnCount}
              glyphKey={glyphKey}
              guidePalette={guidePalette}
              isVisuallyActive={isVisuallyActive}
              progress={progress}
              progressValue={IS_WEB ? webProgress : undefined}
              showBackground={showBackground}
              size={size}
              strokeFill={strokeFill}
              strokeRenderList={strokeRenderList}
              upcomingFill={upcomingFill}
            />
            {children}
          </View>
        </View>
      </Pressable>
      {showStrokeControls && showPlayer && (
        <View
          className="mt-2 w-full flex-row flex-wrap items-center justify-center gap-2 rounded-xs border border-border bg-surface px-3 py-2"
          style={{ maxWidth: Math.max(size, 220) }}
        >
          {strokeNumberList.map((index) => (
            <StrokeNumberButton
              index={index}
              isActive={index === activeStrokeIndex}
              isDarkMode={isDarkMode}
              key={index}
              onPress={playSingleStroke}
            />
          ))}
        </View>
      )}
    </View>
  );
}

/** Native step canvases share the master timeline without sending animation frames to React. */
function GraphemeSvgGlyphStrokeStep({
  canvasProps,
  index,
  isActive,
  onPress,
}: {
  canvasProps: GraphemeGlyphCanvasProps;
  index: number;
  isActive: boolean;
  onPress: (index: number) => void;
}) {
  const { progress, progressValue, strokeRenderList } = canvasProps;
  const totalStrokes = strokeRenderList.length;
  const stepProgress = useDerivedValue(() =>
    getGraphemeSvgGlyphStepProgress(progress.get(), index, totalStrokes),
  );
  return (
    <Pressable
      accessibilityLabel={`Show stroke ${index + 1}`}
      accessibilityRole="button"
      accessibilityState={{ selected: isActive }}
      className="flex-1 items-center gap-1"
      onPress={() => onPress(index)}
    >
      <View className="aspect-square w-full overflow-hidden rounded-xs">
        <GraphemeGlyphCanvas
          {...canvasProps}
          progress={stepProgress}
          progressValue={
            progressValue === undefined
              ? undefined
              : getGraphemeSvgGlyphStepProgress(progressValue, index, totalStrokes)
          }
        />
      </View>
      <Text
        className={`text-xs ${isActive ? "font-semibold text-foreground" : "text-muted-foreground"}`}
      >
        Stroke {index + 1}
      </Text>
    </Pressable>
  );
}

/**
 * One numbered chip. Pressing it draws every earlier stroke instantly and animates stroke `index`.
 * Labels are 1-based for the reader; `index` stays 0-based for the timeline math.
 */
function StrokeNumberButton({
  index,
  isActive,
  isDarkMode,
  onPress,
}: {
  index: number;
  isActive: boolean;
  isDarkMode: boolean;
  onPress: (index: number) => void;
}) {
  const handlePress = useCallback(() => {
    onPress(index);
  }, [index, onPress]);

  const activeClassName = isDarkMode ? "bg-[#F0F4FA]" : "bg-[#111827]";
  const inactiveClassName = isDarkMode ? "bg-[#1F2937]" : "bg-[#F3F4F6]";
  const activeTextClassName = isDarkMode ? "text-[#111827]" : "text-[#F0F4FA]";
  const inactiveTextClassName = isDarkMode ? "text-[#F0F4FA]" : "text-[#111827]";

  return (
    <Pressable
      accessibilityLabel={`Show stroke ${index + 1}`}
      accessibilityRole="button"
      className={`size-7 items-center justify-center rounded-full ${
        isActive ? activeClassName : inactiveClassName
      }`}
      hitSlop={4}
      onPress={handlePress}
    >
      <Text
        className={`text-xs font-semibold ${
          isActive ? activeTextClassName : inactiveTextClassName
        }`}
      >
        {index + 1}
      </Text>
    </Pressable>
  );
}
